import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { FaChartLine } from 'react-icons/fa';
import { XMarkIcon } from '@heroicons/react/24/outline';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const ClientMisc = ({ filters }) => {
  const [isDark, setIsDark] = useState(false);
  const [focus, setFocus] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!accessToken) {
        console.warn("No access token, skipping fetch");
        return;
      }

      setLoading(true);

      try {
        const baseUrl = getRoundRobinBaseUrl();
        if (!baseUrl) {
          console.error("Base URL not found from load balancer");
          return;
        }

        const response = await api.get(
          `${baseUrl}/superAdminDashboard/getSuperAdminClientWeelklyMiscCount`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: filters || {},
          }
        );

        const weeklyData = response.data?.data?.formattedWeeklyData || [];
        const labels = weeklyData.map((item) => item.day);
        const values = weeklyData.map((item) => item.count);

        if (labels.length === 0 || values.every(val => val === 0)) {
          setChartData(null);
        } else {
          setChartData({
            labels,
            datasets: [
              {
                label: 'Weekly MISC Count',
                data: values,
                borderColor: '#EE1D23',
                backgroundColor: isDark ? 'rgba(239,68,68,0.1)' : 'rgba(238,29,35,0.08)',
                pointBackgroundColor: '#1A2B5C',
                fill: true,
                tension: 0.4,
              },
            ],
          });
        }
      } catch (error) {
        console.error('Error fetching formattedWeeklyData:', error);
        setChartData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, accessToken, isDark]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        ticks: { color: isDark ? '#E5E7EB' : '#1A2B5C' },
        grid: { display: false },
      },
      y: {
        ticks: { color: isDark ? '#E5E7EB' : '#1A2B5C' },
        grid: { color: isDark ? '#334155' : '#E2E8F0' },
      },
    },
  };

  const CardWrapper = ({ children }) => (
    <div
      className={`relative w-full max-w-6xl mx-auto p-6 rounded-[2rem] overflow-hidden border-2 backdrop-blur-2xl
      shadow-[0_30px_120px_rgba(0,0,0,0.1)] ${
        isDark
          ? 'bg-[#1A2B5C]/90 border-slate-700 text-white'
          : 'bg-white/60 border-gray-200 text-[#1A2B5C]'
      }`}
    >
      <div className="absolute inset-0 rounded-[2rem] pointer-events-none border-4 border-transparent z-10 animate-border-glow" />
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent z-0 opacity-50 pointer-events-none rounded-[2rem]" />
      <div className="relative z-20">{children}</div>
    </div>
  );

  const NoDataMessage = () => (
    <div className="flex flex-col items-center justify-center h-full text-center animate-fadeIn">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-16 w-16 text-[#EE1D23]/70 mb-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 4.75V3m0 18v-1.75M6.364 6.364l-1.061-1.061m12.728 12.728l-1.061-1.061M4.75 12H3m18 0h-1.75M6.364 17.636l-1.061 1.061m12.728-12.728l-1.061 1.061"
        />
      </svg>
      <div className="text-lg font-semibold text-[#1A2B5C] mb-1">No Data Available</div>
      <p className="text-sm text-gray-500 max-w-sm">
        No MISC weekly data found. Try a different filter or check again later.
      </p>
    </div>
  );

  const Header = () => (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <div className="bg-white text-[#1A2B5C] p-2 rounded-full text-xl shadow-md animate-pulse">
          <FaChartLine />
        </div>
        <div>
          <h2 className="text-lg font-bold uppercase tracking-wide">Client MISC</h2>
          <p className="text-sm text-gray-500 dark:text-gray-300">Weekly Trend</p>
        </div>
      </div>
      <button
        onClick={() => setFocus(true)}
        className="px-4 py-1.5 text-xs font-medium rounded-md border border-[#1A2B5C] text-[#1A2B5C] hover:bg-[#1A2B5C] hover:text-white transition"
      >
        Focus Mode
      </button>
    </div>
  );

  return (
    <>
      {!focus && (
        <CardWrapper>
          <Header />
          <div className="h-64">
            {loading ? (
              <div className="flex justify-center items-center h-full text-gray-400">Loading...</div>
            ) : chartData ? (
              <Line data={chartData} options={options} />
            ) : (
              <NoDataMessage />
            )}
          </div>
        </CardWrapper>
      )}

      {focus && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <CardWrapper>
            <button
              onClick={() => setFocus(false)}
              className="absolute top-5 right-5 bg-white shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23]"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <FaChartLine className="text-[#EE1D23]" />
                <h2 className="text-2xl font-bold tracking-wide">Client MISC Weekly Trend</h2>
              </div>
              <div className="h-2 w-32 rounded-full bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C]" />
            </div>
            <div className="h-[65vh]">
              {loading ? (
                <div className="flex justify-center items-center h-full text-gray-400">Loading...</div>
              ) : chartData ? (
                <Line data={chartData} options={options} />
              ) : (
                <NoDataMessage />
              )}
            </div>
          </CardWrapper>
        </div>
      )}
    </>
  );
};

export default ClientMisc;
